// src/pages/ApprovalsListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { getDatabase, onValue, ref, off } from "firebase/database";
import UElogo from "../assets/UElogo_horizontal.png";
import UElogo2 from "../assets/logo_horizontal.png";
import { ArrowBigLeft } from "lucide-react";
import { EMAILJS } from "../config/emailConfig";
import { fetchAdminLevels, getAdminLevel } from "../utils/userAccess";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import AppTopBar from "../components/AppTopBar";
import AppFooter from "../components/AppFooter";

// --- UI display helpers (do NOT use for keys/comparisons) ---
function formatDateDMY(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
function formatDateTimeDMY(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

// dd/MM/yyyy HH:mm (no seconds)
const fmtDMY_HM = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};


function Chip({ status }) {
  const map = {
    pending: "bg-yellow-600",
    approved: "bg-green-700",
    rejected: "bg-red-700",
  };
  return (
    <span className={`${map[status] || "bg-gray-700"} text-white text-xs px-2 py-1 rounded`}>
      {status}
    </span>
  );
}

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAaR9ZU1w_bFxMKMbNxu-9eBpuOOGm1Eow",
  authDomain: "bookingappv1-d1e2d.firebaseapp.com",
  databaseURL: "https://bookingappv1-d1e2d-default-rtdb.firebaseio.com/",
  projectId: "bookingappv1-d1e2d",
  storageBucket: "bookingappv1-d1e2d.appspot.com",
  messagingSenderId: "134532713601",
  appId: "1:134532713601:web:5ad89fec3f4bf09b519180",
};
if (!getApps().length) initializeApp(firebaseConfig);


const toGB = (v) => (v ? new Date(v).toLocaleString("en-GB", { hour12: false }) : "");

const reviewerText = (r) => {
  // Prefer the new shape
  let by = r.decision?.by;
  // Fallbacks for older data
  if (!by) by = r.reviewedBy || r.reviewBy || r.approvedBy || r.rejectedBy || r.approver || r.reviewer;

  if (!by) return "";
  if (typeof by === "string") return by; // old records stored just an email
  return by.email || by.mail || "";};

const commentText = (r) =>
  r.decision?.reason ??
  r.rejectJustification ??
  r.rejectionReason ??
  r.comment ??
  r.note ??
  "";

const motiveText = (r) =>
  r.motive ?? r.reason ?? r.purpose ?? "";


export default function ApprovalsListPage() {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);
  const [adminLevel, setAdminLevel] = useState(null);
  const [items, setItems] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState("pending"); // default view

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u || null);
      if (u?.email) {
        try {
          const map = await fetchAdminLevels();
          setAdminLevel(getAdminLevel(u.email, map));
        } catch {
          setAdminLevel(null);
        }
      }
    });
    return () => unsub();
  }, []);

  const isApprover = useMemo(() => {
    const matchEmail =
      (authUser?.email || "").toLowerCase() ===
        (EMAILJS?.approvals?.approverEmail || "").toLowerCase();    return matchEmail || adminLevel === 1 || adminLevel === 0;
  }, [authUser, adminLevel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".menu-dropdown")) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    if (!isApprover) return;
    const db = getDatabase();
    const r = ref(db, "approvalRequests");
    const cb = (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val).map(([id, v]) => ({ id, ...v }));
      const toMs = (v) =>
        typeof v === "string" ? Date.parse(v) :
          typeof v === "number" ? v : 0;
      arr.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));

      setItems(arr);
    };
    onValue(r, cb);
    return () => off(r, "value", cb);
  }, [isApprover]);

  if (!authUser) {
    return (
      <div className="flex flex-col p-6 pt-28 min-h-screen bg-gray-800 text-gray-100">
        <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
          <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />
        </div>
        <p>Please sign in to view approvals.</p>
      </div>
    );
  }
  if (!isApprover) {
    return (
      <div className="flex flex-col p-6 pt-28 min-h-screen bg-gray-800 text-gray-100">
        <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
          <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />
          <button onClick={() => navigate(-1)} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded">
            <ArrowBigLeft className="w-5 h-5 text-white" />
          </button>
        </div>
        <p className="text-red-400">You are not allowed to view this page.</p>
      </div>
    );
  }

  const filtered = items.filter((i) => (filter === "all" ? true : i.status === filter));

  // Filter button styles
  const baseClasses = {
    pending: "bg-gray-700 hover:bg-gray-800",
    approved: "bg-gray-700 hover:bg-gray-800",
    rejected: "bg-gray-700 hover:bg-gray-800",
    all: "bg-gray-700 hover:bg-gray-800",
  };
  const activeClasses = {
    pending: "bg-yellow-700 ring-2 ring-yellow-600",
    approved: "bg-green-700 ring-2 ring-green-600",
    rejected: "bg-red-700 ring-2 ring-red-600",
    all: "bg-purple-700 ring-2 ring-purple-600",
  };

  // Format helpers
const fmtDT = (v) => (v ? new Date(v).toLocaleString("en-GB") : "");
const fmtDateMaybe = (v) => {
  if (!v) return "";
  if (typeof v === "number") return new Date(v).toLocaleString("en-GB");
  return new Date(v).toLocaleString("en-GB");
};

const toGB = (v) =>
  v ? new Date(v).toLocaleString("en-GB", { hour12: false }) : "";

const exportXLSX = () => {
  const headers = [
    "Status",
    "Room",
    "Requester",
    "From",
    "To",
    "Motive",
    "Justification / Comment",
    "Reviewed by",
  ];

  const rows = filtered.map((r) => [
    r.status ?? "",
    r.room ?? "",
    `${r.requestedBy?.name ?? ""}; ${r.requestedBy?.email ?? ""}`,
    fmtDMY_HM(r.period?.startISO),
    fmtDMY_HM(r.period?.endISO),
    motiveText(r),
    commentText(r),
    reviewerText(r),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Approvals");
  XLSX.writeFile(wb, `approvals_${filter}.xlsx`);
};

const exportPDF = () => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 15;
  const startY = 30;

  // Header
  doc.setFontSize(12);
  doc.text("Approvals list", 10, 20);
  const img = new Image();
  img.src = UElogo2;
  doc.addImage(img, "PNG", pageWidth - 80, 10, 70, 15);

  const head = [[
    "Status",
    "Room",
    "Requester",
    "From",
    "To",
    "Motive",
    "Justification / Comment",
    "Reviewed by",
  ]];

  const body = filtered.map((r) => [
    r.status ?? "",
    r.room ?? "",
    `${r.requestedBy?.name ?? ""}; ${r.requestedBy?.email ?? ""}`,
    fmtDMY_HM(r.period?.startISO),
    fmtDMY_HM(r.period?.endISO),
    motiveText(r),
    commentText(r),
    reviewerText(r),
  ]);

  // widths sum to ~182mm (A4 usable with 14mm margins)
  const colWidths = [14, 24, 36, 16, 16, 28, 28, 20];

  autoTable(doc, {
    startY,
    head,
    body,
    styles: { fontSize: 9 },
    headStyles: { fontSize: 7, fillColor: [55, 94, 151], textColor: 255, halign: "left" },
    bodyStyles: { fontSize: 7 }, 
    columnStyles: {
      0: { cellWidth: colWidths[0] },
      1: { cellWidth: colWidths[1] },
      2: { cellWidth: colWidths[2] },
      3: { cellWidth: colWidths[3] },
      4: { cellWidth: colWidths[4] },
      5: { cellWidth: colWidths[5] },
      6: { cellWidth: colWidths[6] },
      7: { cellWidth: colWidths[7] },
    },
    didDrawPage: () => {
      const pageCount = doc.internal.getNumberOfPages();
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      const exportUser = authUser?.displayName || authUser?.email || "Unknown";
      const exportTime = new Date().toLocaleString("en-GB", { hour12: false });

      doc.setFontSize(8);
      doc.text(`Exported by: ${exportUser} on ${exportTime}`, margin, pageHeight - 8);
      doc.text(`${pageNumber}/${pageCount}`, pageWidth - margin, pageHeight - 8, { align: "right" });
    },
  });

  const pdfBlob = doc.output("blob");
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, "_blank");
};


  return (
    <div className="flex flex-col p-6 pt-28 w-full bg-gray-800 text-gray-100 min-h-screen">
      <AppTopBar
        showBell={false}
        onExportXLSX={exportXLSX}
        onExportPDF={exportPDF}
      />

      <h1 className="text-xl font-bold mb-4">Approvals</h1>

      {/* Filters */}
      <div className="mb-4 flex gap-2">
        {["pending", "approved", "rejected", "all"].map((f) => {
          const isActive = filter === f;
          const color = isActive ? activeClasses[f] : baseClasses[f];
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={isActive}
              className={`px-3 py-1 rounded text-white transition ${color}`}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-gray-300">No requests.</p>}
        {filtered.map((r) => (
          <div
            key={r.id}
            onClick={() => navigate(`/approvals/${r.id}`)}
            className="cursor-pointer border border-gray-700 rounded p-3 bg-gray-900 hover:bg-gray-800"
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold">{r.room}</div>
              <Chip status={r.status} />
            </div>
            <div className="text-sm text-gray-300 mt-1">
              {r.requestedBy?.name} ({r.requestedBy?.email})
            </div>
            <div className="text-xs text-gray-400 mt-1">
              From: {formatDateTimeDMY(r.period?.startISO)} — To: {formatDateTimeDMY(r.period?.endISO)}
            </div>
          </div>
        ))}
      </div>
      <AppFooter />
    </div>
  );
}

